import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ExposePDF from './ExposePDF';

const LOGO = 'https://public.readdy.ai/ai/img_res/1263c92f-fd0b-47c8-8fec-161a92cdc272.png';

export default function SiteNavBar() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = pathname === '/';
  const currentLang = i18n.language?.startsWith('de') ? 'de' : 'en';

  const navItems = [
    { label: t('nav.residence'), to: '/' },
    { label: t('nav.gallery'), to: '/galerie' },
    { label: t('nav.highlights'), to: '/highlights' },
    { label: t('nav.location'), to: '/lage' },
    { label: t('nav.contact'), to: '/kontakt' },
  ];

  useEffect(() => {
    if (!isHome) { setScrolled(true); return; }
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const solidNav = !isHome || scrolled;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${solidNav ? 'bg-[rgba(250,250,247,0.97)] backdrop-blur-sm border-b border-[#E5DDD3] py-3' : 'bg-transparent py-5'}`}>
      <div className="relative max-w-7xl mx-auto px-8 flex items-center justify-between gap-6">

        {/* Left — Villa Name */}
        <Link to="/" className="shrink-0 flex flex-col leading-none group">
          <span
            className={`font-cormorant font-light tracking-[0.12em] transition-colors duration-300 ${solidNav ? 'text-[#1C1C1C]' : 'text-white'}`}
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem', letterSpacing: '0.1em' }}
          >
            The Obsidian Villa
          </span>
          <span
            className={`font-sans text-[8px] tracking-[0.32em] uppercase transition-colors duration-300 mt-0.5 ${solidNav ? 'text-[#B59A6A]' : 'text-[#B59A6A]/90'}`}
          >
            Private Residence
          </span>
        </Link>

        {/* Desktop nav links — absolut zentriert */}
        <div className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
          {navItems.map((item) => {
            const active = pathname === item.to;
            return (
              <Link key={item.to} to={item.to}
                className={`font-sans text-[11px] tracking-widest uppercase whitespace-nowrap transition-colors duration-300 relative group ${active ? 'text-[#B59A6A]' : solidNav ? 'text-[#1C1C1C] hover:text-[#B59A6A]' : 'text-white/90 hover:text-[#B59A6A]'}`}
              >
                {item.label}
                <span className={`absolute -bottom-1 left-0 h-px bg-[#B59A6A] transition-all duration-300 ${active ? 'w-full' : 'w-0 group-hover:w-full'}`} />
              </Link>
            );
          })}

          {/* Exposé — als dezenter Nav-Link direkt bei den anderen Links */}
          <div className={`flex items-center gap-1.5 font-sans text-[11px] tracking-widest uppercase whitespace-nowrap transition-colors duration-300 relative ${solidNav ? 'text-[#6B6460]' : 'text-white/70'}`}>
            <div className={`w-px h-3 ${solidNav ? 'bg-[#E5DDD3]' : 'bg-white/20'} mr-2`} />
            <ExposePDF solidNav={solidNav} />
          </div>
        </div>

        {/* Right side: lang toggle + CTA */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          {/* Language toggle */}
          <div className={`flex items-center gap-0 border transition-colors duration-300 ${solidNav ? 'border-[#E5DDD3]' : 'border-white/30'}`}>
            <button onClick={() => i18n.changeLanguage('de')}
              className={`px-2.5 py-1.5 font-sans text-[10px] tracking-widest uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap ${currentLang === 'de' ? 'text-[#B59A6A]' : solidNav ? 'text-[#6B6460] hover:text-[#1C1C1C]' : 'text-white/60 hover:text-white'}`}>
              DE
            </button>
            <span className={`text-[10px] ${solidNav ? 'text-[#E5DDD3]' : 'text-white/20'}`}>·</span>
            <button onClick={() => i18n.changeLanguage('en')}
              className={`px-2.5 py-1.5 font-sans text-[10px] tracking-widest uppercase transition-colors duration-200 cursor-pointer whitespace-nowrap ${currentLang === 'en' ? 'text-[#B59A6A]' : solidNav ? 'text-[#6B6460] hover:text-[#1C1C1C]' : 'text-white/60 hover:text-white'}`}>
              EN
            </button>
          </div>

          {/* Termin CTA */}
          <Link to="/termin" className="gold-btn text-[10px] tracking-widest whitespace-nowrap">
            {t('nav.appointment')}
          </Link>
        </div>

        {/* Mobile toggle */}
        <button className={`lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${solidNav ? 'text-[#1C1C1C]' : 'text-white'}`}
          onClick={() => setMenuOpen((o) => !o)} aria-label="Menü">
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-[#FAFAF7] border-t border-[#E5DDD3] px-8 py-6 flex flex-col gap-5">
          {navItems.map((item) => (
            <Link key={item.to} to={item.to}
              className={`font-sans text-xs tracking-widest uppercase transition-colors ${pathname === item.to ? 'text-[#B59A6A]' : 'text-[#1C1C1C] hover:text-[#B59A6A]'}`}>
              {item.label}
            </Link>
          ))}
          <ExposePDF solidNav={true} />
          <div className="flex items-center gap-4 pt-2 border-t border-[#E5DDD3]">
            <button onClick={() => i18n.changeLanguage('de')} className={`font-sans text-xs tracking-widest uppercase cursor-pointer ${currentLang === 'de' ? 'text-[#B59A6A]' : 'text-[#6B6460]'}`}>DE</button>
            <span className="text-[#E5DDD3]">·</span>
            <button onClick={() => i18n.changeLanguage('en')} className={`font-sans text-xs tracking-widest uppercase cursor-pointer ${currentLang === 'en' ? 'text-[#B59A6A]' : 'text-[#6B6460]'}`}>EN</button>
          </div>
          <Link to="/termin" className="gold-btn text-center text-[10px]">{t('nav.appointment')}</Link>
        </div>
      )}
    </nav>
  );
}
